"use strict";
/**
 * The live game-state fold for NBA taps: ai/nba/N8-taps/STATE-FOLD-V2.md is the rule, and
 * model/tests/nba/fixtures/game-state-v2.json holds this and model/wp/nba/tap_state_live.py equal.
 * Scoring facts count from every received tap; context moves only on a current tap (the session of
 * the latest session_start). The internal FT trip uses ft-pending's transition table under this
 * fold's session rule; it never replaces ft-pending's production field.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.NbaTapStateFolder = exports.STATE_VERSION = void 0;
exports.nbaStateRefold = nbaStateRefold;
exports.nbaStateSteps = nbaStateSteps;
const ft_pending_1 = require("./ft-pending");
const tap_vocabulary_1 = require("./tap-vocabulary");
/** The rule's version (STATE-FOLD-V2.md), carried by the export. */
exports.STATE_VERSION = 1;
const KNOWN = new Set(tap_vocabulary_1.NBA_ALL_KINDS);
const POSSESSION = new Set([
    "shot_2", "shot_3", "made_2", "made_3", "missed", "ball", "off_rebound", "ft_trip_1", "ft_trip_2", "ft_trip_3", "turnover", "def_rebound", "ft_unsure",
]);
/** What a late tap, or a late UNDO's target, flags as uncertain context (readings 8, 11). */
const FLAGS = (kind) => kind !== null && (POSSESSION.has(kind) || kind === "ft_made" || kind === "ft_missed" || kind === "period_next");
/** A Map, not an object: a kind such as `toString` must score 0. */
const POINTS = new Map([["made_2", 2], ["made_3", 3], ["ft_made", 1], ["ft_tech_made", 1]]);
/** v1 taps that make a later v2 session a mixed game (reading 12). */
const NOT_V1_PLAY = new Set(["session_start", "pull", "undo", ...tap_vocabulary_1.NBA_TRACK_KINDS]);
const NO_CTX = { kind: "none" };
const other = (s) => (s === "home" ? "away" : "home");
const asSide = (v) => (v === "home" || v === "away" ? v : null);
const idOf = (v) => (typeof v === "string" ? v : null);
const initialContext = () => ({
    poss: null, ptype: "period_start", inferred: false, ctx: NO_CTX, period: 1, first: null, tipAnswer: null, possTapSeen: false,
    fromTip: false, contextUncertain: false, note: null, ft: { trip: { kind: "unknown" }, tech: null, reason: null },
});
const opening = (c) => c.tipAnswer === true && !c.possTapSeen && c.period === 1;
function setPoss(c, poss, ptype, ctx) {
    c.poss = poss;
    c.ptype = ptype;
    c.inferred = false;
    c.ctx = ctx;
}
function periodNext(c, target) {
    if (c.period !== null && target !== null && target !== undefined && target !== c.period + 1) {
        c.contextUncertain = true;
        c.note = "period-mismatch";
        return;
    }
    c.ft = (0, ft_pending_1.ftTransition)(c.ft, { kind: "period_next", side: null });
    c.ptype = "period_start";
    c.inferred = false;
    c.ctx = NO_CTX;
    if (c.period === null) {
        c.poss = null;
        c.note = "period-unknown";
        return;
    }
    c.period += 1;
    c.poss = c.first === null || c.period > 4 ? null : c.period === 4 ? c.first : other(c.first);
    if (c.poss !== null)
        c.contextUncertain = false;
}
/** A current tap of a recognized kind with its side present. */
function applyCurrent(c, r) {
    const { kind } = r;
    if (kind === "period_next")
        return periodNext(c, r.tap.clockPeriod);
    const side = r.side; // every kind below is sided, checked by the caller
    const before = c.ft;
    c.ft = (0, ft_pending_1.ftTransition)(before, { kind, side });
    switch (kind) {
        case "shot_2":
        case "shot_3":
            c.poss = side;
            c.ctx = { kind: "shot", side, v: kind === "shot_2" ? 2 : 3 };
            break;
        case "missed":
            setPoss(c, side, "rebound_pending", { kind: "loose", side });
            break;
        case "off_rebound":
            setPoss(c, side, "after_oreb", NO_CTX);
            break;
        case "ball": {
            const ptype = c.ptype === "period_start" && c.poss === null ? "period_start"
                : c.ctx.kind === "none" ? "after_dead_tov" : c.ctx.side === side ? "after_oreb" : "after_dreb";
            if (opening(c))
                c.first = side;
            setPoss(c, side, ptype, NO_CTX);
            c.inferred = true;
            break;
        }
        case "made_2":
        case "made_3":
            setPoss(c, other(side), "after_make", NO_CTX);
            break;
        case "ft_trip_1":
        case "ft_trip_2":
        case "ft_trip_3":
        case "ft_unsure":
            setPoss(c, side, "ft_pending", NO_CTX);
            break;
        case "ft_made":
        case "ft_missed":
            // Only a known transition moves the ball (reading 10).
            if (before.trip.kind === "open" && c.ft.trip.kind === "none") {
                if (kind === "ft_made")
                    setPoss(c, other(side), "after_make", NO_CTX);
                else
                    setPoss(c, side, "rebound_pending", { kind: "loose", side });
            }
            else if (c.ft.trip.kind === "open")
                setPoss(c, side, "ft_pending", c.ctx);
            break;
        case "turnover":
            setPoss(c, other(side), "after_tov", NO_CTX);
            break;
        case "def_rebound":
            setPoss(c, side, "after_dreb", NO_CTX);
            break;
        default: // the technical kinds: the trip only
    }
    if (POSSESSION.has(kind))
        c.contextUncertain = false;
}
/** One received tap's effect on the context. Scoring and `pulled` are summed apart. */
function applyContext(c, r) {
    const { kind } = r;
    c.note = null;
    if (kind === "session_start") {
        c.ft = { trip: { kind: "unknown" }, tech: null, reason: "reset" };
        if (r.mixed) {
            c.period = null;
            c.poss = null;
            c.first = null;
        }
        return;
    }
    if (kind === "undo") {
        if (r.undo === null)
            c.note = "invalid-undo";
        else if (!r.undo.current && FLAGS(r.undo.target.kind))
            c.contextUncertain = true;
        return;
    }
    if (kind === "tip_confirm") {
        if (c.possTapSeen)
            c.note = "tip-after-possession";
        else {
            c.tipAnswer = typeof r.tap.atTip === "boolean" ? r.tap.atTip : null;
            c.fromTip = c.tipAnswer === true;
        }
        return;
    }
    if (kind === "pull" || kind === "resume" || tap_vocabulary_1.NBA_TRACK_KINDS.has(kind) || tap_vocabulary_1.NBA_CLOCK_KINDS.has(kind))
        return;
    if (!KNOWN.has(kind) || (tap_vocabulary_1.NBA_SIDED_TAP_KINDS.has(kind) && r.side === null)) {
        c.note = "unrecognized";
        return;
    }
    if (r.current)
        applyCurrent(c, r);
    else if (FLAGS(kind))
        c.contextUncertain = true;
    // fromTip latches here: only tip_confirm moves it, and only before this (reading 1).
    if (POSSESSION.has(kind))
        c.possTapSeen = true;
}
/** Receipt: retries, the current session, the mixed-game test and UNDO validity (§19a.2, §19a.3). */
class Receiver {
    constructor() {
        this.records = [];
        /** Every valid UNDO's target, current or late. */
        this.undone = new Set();
        this.byId = new Map();
        /** undefined before any session_start: every tap is current (reading 9). */
        this.session = undefined;
        this.v1Played = false;
    }
    /** The record for a new tap, or null for a retry or a non-object. */
    receive(tap) {
        if (tap === null || typeof tap !== "object")
            return null;
        const tapId = idOf(tap.tapId);
        if (this.byId.has(tapId))
            return null;
        const kind = typeof tap.kind === "string" ? tap.kind : "";
        const sessionId = idOf(tap.sessionId);
        const version = (0, tap_vocabulary_1.nbaPadVersion)(typeof tap.padVersion === "number" ? tap.padVersion : null);
        const rec = { tapId: tapId ?? "", kind, side: asSide(tap.side), sessionId, tap, current: true, mixed: false, undo: null };
        if (kind === "session_start") {
            rec.mixed = version >= 2 && this.v1Played;
            this.session = sessionId;
        }
        else
            rec.current = this.session === undefined || sessionId === this.session;
        if (version === 1 && !NOT_V1_PLAY.has(kind))
            this.v1Played = true;
        if (kind === "undo") {
            const target = this.byId.get(idOf(tap.undoesTapId));
            if (target !== undefined && target.sessionId === sessionId && (0, ft_pending_1.nbaUndoReachable)(target.tap) && !this.undone.has(target.tapId)) {
                rec.undo = { target, current: rec.current };
                this.undone.add(target.tapId);
            }
        }
        this.byId.set(tapId, rec);
        this.records.push(rec);
        return rec;
    }
}
const points = (r) => (r.side === null ? 0 : POINTS.get(r.kind) ?? 0);
/** The latest pull or resume not undone. */
function pulledOf(records, undone) {
    for (let i = records.length - 1; i >= 0; i--) {
        const r = records[i];
        if ((r.kind === "pull" || r.kind === "resume") && !undone.has(r.tapId))
            return r.kind === "pull";
    }
    return false;
}
function snapshot(c, margin, total, pulled) {
    return {
        margin, total, poss: c.poss, ptype: c.ptype, inferred: c.inferred, ctx: { ...c.ctx }, period: c.period, first: c.first,
        tipAnswer: c.tipAnswer, possTapSeen: c.possTapSeen, opening: opening(c), fromTip: c.fromTip, contextUncertain: c.contextUncertain,
        pulled, note: c.note,
    };
}
/** Context from scratch: every record in order, without the targets of current UNDOs (reading 4). */
function refoldContext(records) {
    const skip = new Set();
    for (const r of records)
        if (r.undo?.current)
            skip.add(r.undo.target.tapId);
    const c = initialContext();
    for (const r of records)
        if (!skip.has(r.tapId))
            applyContext(c, r);
    return c;
}
/**
 * The incremental fold: taps in publication order, one state per received tap. A current valid
 * UNDO re-folds the context from scratch; nothing else looks back. Never throws.
 */
class NbaTapStateFolder {
    constructor() {
        this.rx = new Receiver();
        this.c = initialContext();
        this.margin = 0;
        this.total = 0;
        this.pulled = false;
    }
    /** The state after this tap, or null for a retry. */
    apply(tap) {
        const r = this.rx.receive(tap);
        if (r === null)
            return null;
        const p = points(r);
        this.margin += r.side === "away" ? -p : p;
        this.total += p;
        if (r.kind === "pull" || r.kind === "resume")
            this.pulled = r.kind === "pull";
        if (r.undo !== null) {
            const t = points(r.undo.target);
            this.margin -= r.undo.target.side === "away" ? -t : t;
            this.total -= t;
            this.pulled = pulledOf(this.rx.records, this.rx.undone);
        }
        if (r.undo?.current)
            this.c = refoldContext(this.rx.records);
        else
            applyContext(this.c, r);
        return snapshot(this.c, this.margin, this.total, this.pulled);
    }
}
exports.NbaTapStateFolder = NbaTapStateFolder;
/** The state after the last received tap, folded from scratch in one pass. */
function nbaStateRefold(taps) {
    const rx = new Receiver();
    for (const t of taps)
        rx.receive(t);
    let margin = 0;
    let total = 0;
    for (const r of rx.records) {
        if (rx.undone.has(r.tapId))
            continue;
        const p = points(r);
        margin += r.side === "away" ? -p : p;
        total += p;
    }
    return snapshot(refoldContext(rx.records), margin, total, pulledOf(rx.records, rx.undone));
}
/** The append-only state history: one step per received tap; retries emit none. */
function nbaStateSteps(taps) {
    const folder = new NbaTapStateFolder();
    const steps = [];
    for (const t of taps) {
        const state = folder.apply(t);
        if (state !== null)
            steps.push({ tapId: t.tapId, state });
    }
    return steps;
}
